import React, { useState, useEffect } from 'react';
import contentService from '../services/contentService';
import './InfluencerModal.css';

const InfluencerModal = ({ influencer, onClose }) => {
  const [formData, setFormData] = useState({
    name: influencer?.name || '',
    username: influencer?.username || '',
    email: influencer?.email || '',
    phone: influencer?.phone || '',
    notes: influencer?.notes || '',
    profile_image_url: influencer?.profile_image_url || '',
    is_active: influencer?.is_active !== undefined ? influencer.is_active : true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fetchingImage, setFetchingImage] = useState(false);
  const [imageFound, setImageFound] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [imageSaved, setImageSaved] = useState(false);

  // Inicializar imageFound e imageSaved si el influencer ya tiene imágenes
  useEffect(() => {
    if (influencer?.profile_image_url) {
      setImageFound(true);
    }
    if (influencer?.profile_image_url_saved) {
      setImageSaved(true);
    }
  }, [influencer]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    }

    // Validar email si se proporciona
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validar teléfono básico si se proporciona
    if (formData.phone && formData.phone.length < 8) {
      newErrors.phone = 'El teléfono debe tener al menos 8 dígitos';
    }

    // Validar URL de imagen si se proporciona
    if (formData.profile_image_url && !/^https?:\/\/.+\..+/.test(formData.profile_image_url)) {
      newErrors.profile_image_url = 'La URL de la imagen no es válida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        email: formData.email.trim() || '',
        phone: formData.phone.trim() || '',
        notes: formData.notes.trim() || '',
        profile_image_url: formData.profile_image_url.trim() || '',
        is_active: formData.is_active
      };

      if (influencer) {
        // Editar
        console.log('📝 Editando influencer ID:', influencer.id);
        console.log('📝 Datos a enviar:', dataToSend);
        await contentService.updateInfluencer(influencer.id, dataToSend);
        alert('✅ Influencer actualizado exitosamente');
      } else {
        // Crear
        console.log('➕ Creando nuevo influencer');
        console.log('📝 Datos a enviar:', dataToSend);
        await contentService.createInfluencer(dataToSend);
        alert('✅ Influencer creado exitosamente');
      }

      onClose();
    } catch (error) {
      console.error('❌ Error saving influencer:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);

      // Intentar extraer el mensaje de error más específico
      let errorMessage = 'Error al guardar el influencer';

      if (error.response?.data) {
        const errorData = error.response.data;

        // Si hay errores de campo específicos
        if (typeof errorData === 'object' && !errorData.detail) {
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
          errorMessage = fieldErrors;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }

      alert(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const fetchProfileImage = async () => {
    if (!formData.username.trim()) {
      return;
    }

    setFetchingImage(true);
    setImageFound(false);

    try {
      const username = formData.username.trim().replace('@', '');
      const params = { username };

      console.log('🔍 Buscando imagen de perfil para:', username);
      const response = await contentService.getIGBusinessDiscovery(params);

      console.log('📦 Respuesta completa:', response);
      console.log('📦 response.data:', response.data);

      // La respuesta viene en business_discovery
      const businessDiscovery = response.data?.business_discovery;
      const profilePictureUrl = businessDiscovery?.profile_picture_url;

      console.log('📦 business_discovery:', businessDiscovery);
      console.log('📦 profile_picture_url:', profilePictureUrl);

      if (profilePictureUrl) {
        console.log('✅ URL de imagen encontrada:', profilePictureUrl);
        setFormData(prev => ({
          ...prev,
          profile_image_url: profilePictureUrl
        }));
        setImageFound(true);
        alert('✅ Imagen de perfil encontrada');
      } else {
        console.warn('⚠️ No se encontró profile_picture_url en la respuesta');
        console.log('📋 Estructura de response.data:', JSON.stringify(response.data, null, 2));
        alert('⚠️ No se encontró la imagen de perfil. Verifica que el usuario sea correcto.');
      }
    } catch (error) {
      console.error('❌ Error fetching profile image:', error);
      console.error('❌ Error response:', error.response?.data);
      alert('❌ Error al buscar la imagen de perfil. Intenta de nuevo.');
    } finally {
      setFetchingImage(false);
    }
  };

  // 🆕 FUNCIÓN PARA GUARDAR IMAGEN EN DIGITALOCEAN SPACES
  const saveProfileImage = async () => {
    if (!influencer?.id) {
      alert('⚠️ Primero debes guardar el influencer antes de guardar la imagen');
      return;
    }

    if (!formData.profile_image_url) {
      alert('⚠️ No hay ninguna URL de imagen para guardar');
      return;
    }

    setSavingImage(true);
    setImageSaved(false);

    try {
      console.log('💾 Guardando imagen en Spaces para influencer ID:', influencer.id);
      const response = await contentService.saveInfluencerProfileImage(influencer.id);

      console.log('✅ Respuesta:', response.data);

      if (response.data.success) {
        setImageSaved(true);
        alert('✅ Imagen guardada exitosamente en DigitalOcean Spaces');
      }
    } catch (error) {
      console.error('❌ Error saving profile image:', error);
      console.error('❌ Error response:', error.response?.data);
      const errorMsg = error.response?.data?.error || 'Error al guardar la imagen';
      alert(`❌ ${errorMsg}`);
    } finally {
      setSavingImage(false);
    }
  };

  // 🆕 FUNCIÓN PARA CONECTAR INSTAGRAM
  const handleConnectInstagram = () => {
    if (!influencer?.id) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');
    const connectUrl = `${backendUrl}/instagram/connect-influencer/${influencer.id}/`;

    console.log('🔗 Abriendo ventana de Instagram:', connectUrl);
    window.open(connectUrl, 'InstagramAuth', 'width=600,height=700');
  };

  // 🆕 FUNCIÓN PARA CONECTAR TIKTOK
  const handleConnectTikTok = () => {
    if (!influencer?.id) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');
    const connectUrl = `${backendUrl}/api/tiktok/connect-influencer/${influencer.id}/`;

    console.log('🔗 Abriendo ventana de TikTok:', connectUrl);
    window.open(connectUrl, 'TikTokAuth', 'width=600,height=700');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="im-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="im-header">
          <h2 className="im-title">{influencer ? 'Editar' : 'Nueva'} Cuenta</h2>
          <button onClick={onClose} className="im-close" disabled={loading} title="Cerrar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="im-body">

          {/* Nombre */}
          <div className="im-field">
            <label className="im-label">Nombre <span className="im-required">*</span></label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Juan Pérez"
              className={`im-input ${errors.name ? 'error' : ''}`}
              disabled={loading}
              maxLength={200}
            />
            {errors.name && <span className="im-error">{errors.name}</span>}
          </div>

          {/* Usuario */}
          <div className="im-field">
            <label className="im-label">Usuario de Instagram <span className="im-required">*</span></label>
            <div className="im-input-row">
              <span className="im-at">@</span>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="usuario_instagram"
                className={`im-input with-at ${errors.username ? 'error' : ''}`}
                disabled={loading}
                maxLength={100}
              />
              <button
                type="button"
                onClick={fetchProfileImage}
                disabled={loading || fetchingImage || !formData.username.trim()}
                className="im-btn-fetch"
              >
                {fetchingImage ? '⏳' : (imageFound ? '✓ Foto' : 'Buscar foto')}
              </button>
            </div>
            {errors.username && <span className="im-error">{errors.username}</span>}
            <span className="im-hint">Sin el símbolo @</span>
          </div>

          {/* Estado Activo */}
          <div className="im-field">
            <label className="im-checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={loading}
                style={{ accentColor: '#71d234', width: 16, height: 16 }}
              />
              <span className="im-checkbox-text">Cuenta activa</span>
            </label>
          </div>

          {/* Footer */}
          <div className="im-footer">
            <button type="button" onClick={onClose} className="estado-btn" disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="estado-btn im-btn-save" disabled={loading}>
              {loading ? 'Guardando...' : (influencer ? 'Guardar cambios' : 'Crear cuenta')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default InfluencerModal;